/**
 * src/lib/agent/scopeSearch.js — TICKET-1148.
 *
 * One search for everything the chat can be narrowed to (projects, leads, tasks,
 * people), shared by the scope picker and the composer's @-mention list.
 * CEO 2026-10-12: "it needs a smarter filter". Plain substring on the name only
 * missed "real life" for "Vincent George - Fybridge Real Life" typed as
 * "george real", missed accents ("fernandez" vs "Fernández"), never matched a
 * task by its project, and listed closed work as high as live work.
 *
 * Rules: accent- and case-insensitive; every word must match somewhere (name or
 * context line), in any order; name hits outrank context hits, word starts
 * outrank mid-word hits, a name that starts with the whole query ranks highest;
 * closed / done / lost items always list after live ones.
 */

export function normalize(s) {
  return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

export function entityLabel(row, type) {
  if (!row) return '';
  switch (type) {
    case 'project': return row.project_name || row.name || '';
    // crm_deals' display name is `opportunity` (no deal_name column).
    case 'deal': return row.deal_name || row.opportunity || row.name || '';
    case 'task': return row.task_name || row.name || '';
    case 'person': return row.display_name || row.full_name || row.email || '';
    default: return '';
  }
}

/** Short context line shown under the name and searched as a weaker match. */
export function entityContext(row, type, appData = {}) {
  if (!row) return '';
  switch (type) {
    case 'project': return [row.status, row.vessel_name].filter(Boolean).join(' · ');
    case 'deal': return [row.status, row.contact_name].filter(Boolean).join(' · ');
    case 'task': {
      const project = (appData.projects || []).find((p) => p?.id === row.project_id);
      return [project?.project_name, row.status, row.due_date].filter(Boolean).join(' · ');
    }
    case 'person': return [row.role, row.email].filter(Boolean).join(' · ');
    default: return '';
  }
}

const CLOSED = /^(done|completed|closed|archived|cancel+ed|lost|perdida|cerrad[ao]|finalized|legacy)$/i;

export function isClosed(row, type) {
  if (!row) return false;
  if (row.archived_at) return true;
  const status = String(row.status || '').trim();
  if (type === 'person') return false;
  return CLOSED.test(status);
}

function wordStarts(hay, token) {
  return hay.startsWith(token) || hay.includes(` ${token}`) || hay.includes(`-${token}`);
}

/** Score one row for a query; 0 = no match. Empty query = everything, live first. */
export function scoreRow(row, type, query, appData) {
  const label = normalize(entityLabel(row, type));
  if (!label) return 0;
  const q = normalize(query);
  if (!q) return 1;
  const ctx = normalize(entityContext(row, type, appData));
  let score = 0;
  for (const token of q.split(/\s+/).filter(Boolean)) {
    if (wordStarts(label, token)) score += 30;
    else if (label.includes(token)) score += 15;
    else if (wordStarts(ctx, token)) score += 8;
    else if (ctx.includes(token)) score += 4;
    else return 0; // every word must match somewhere
  }
  if (label === q) score += 100;
  else if (label.startsWith(q)) score += 40;
  return score;
}

/**
 * Rank rows of the given types for a query.
 * @returns {Array<{type, row, label, context, score}>} best first, capped.
 */
export function searchScope(appData, types, query, { cap = 50, filterRow } = {}) {
  const sources = {
    project: appData?.projects || [],
    deal: appData?.crm || appData?.deals || [],
    task: appData?.tasks || [],
    person: appData?.people || [],
  };
  const out = [];
  for (const type of types) {
    for (const row of sources[type] || []) {
      if (filterRow && !filterRow(row, type)) continue;
      const score = scoreRow(row, type, query, appData);
      if (score > 0) {
        out.push({ type, row, label: entityLabel(row, type), context: entityContext(row, type, appData), score, closed: isClosed(row, type) });
      }
    }
  }
  // Live work always above closed work; then by score, then by name.
  out.sort((a, b) => (a.closed - b.closed) || (b.score - a.score) || a.label.localeCompare(b.label));
  return out.slice(0, cap);
}
